import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import type { Chat } from "../services/chatService";
import { getUserChats } from "../services/chatService";
import styles from "./ChatHistory.module.css";

export function ChatHistory() {
	const { t } = useClientTranslations("chat");
	const { logChat } = useAnalytics();
	const navigate = useNavigate();
	const [chats, setChats] = useState<Chat[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Newest first. Sort by the SAME field we display (createdAt) so the visible
	// dates always read top-to-bottom in descending order — the server order
	// isn't guaranteed and was interleaving older chats (e.g. an Aug-2025 chat
	// above June chats).
	const sortedChats = useMemo(
		() =>
			[...chats].sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			),
		[chats],
	);

	useEffect(() => {
		logChat("history_page_viewed");
	}, [logChat]);

	useEffect(() => {
		async function fetchChats() {
			try {
				setIsLoading(true);
				setError(null);
				const fetchedChats = await getUserChats();
				setChats(fetchedChats);
				logChat("history_loaded", {
					chat_count: fetchedChats.length,
				});
			} catch (err) {
				console.error("Error fetching chats:", err);
				const errorMessage =
					err instanceof Error ? err.message : "Failed to load chats";
				setError(errorMessage);
				logChat("history_error", {
					error: errorMessage,
				});
			} finally {
				setIsLoading(false);
			}
		}

		fetchChats();
	}, [logChat]);

	const handleChatClick = (chatId: string) => {
		// HIPAA: omit chat_title — it is derived from the user's first message
		// (medical free-text). chat_id alone identifies the conversation.
		logChat("history_item_clicked", { chat_id: chatId });
		navigate(`/chat/${chatId}`);
	};

	const handleNewChat = () => {
		logChat("history_new_chat_clicked");
		navigate("/chat?new=true");
	};

	const formatDate = (date: Date) => {
		const chatDate = new Date(date);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (chatDate.toDateString() === today.toDateString()) {
			return t("history.today");
		}
		if (chatDate.toDateString() === yesterday.toDateString()) {
			return t("history.yesterday");
		}

		return chatDate.toLocaleDateString(undefined, {
			month: "short",
			day: "numeric",
			year:
				chatDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
		});
	};

	const extractTextFromContent = (content: string | unknown): string | null => {
		// If content is a string, return it
		if (typeof content === "string") {
			return content;
		}

		// If content is an array (multi-part message), extract text parts
		if (Array.isArray(content)) {
			const textParts = content
				.filter(
					(part): part is { type: string; text: string } =>
						typeof part === "object" &&
						part !== null &&
						"type" in part &&
						part.type === "text" &&
						"text" in part &&
						typeof part.text === "string",
				)
				.map((part) => part.text);
			return textParts.join(" ") || null;
		}

		// If content is an object with a text field, extract it
		if (
			content &&
			typeof content === "object" &&
			"text" in content &&
			typeof content.text === "string"
		) {
			return content.text;
		}

		return null;
	};

	const truncateMessage = (message: string, maxLength = 80) => {
		if (message.length <= maxLength) return message;
		return `${message.substring(0, maxLength)}...`;
	};

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				<div className={styles.container}>
					<h1 className={styles.title}>{t("history.title")}</h1>

					<button
						type="button"
						onClick={handleNewChat}
						className={styles.newChatButton}
					>
						+ {t("history.newChat")}
					</button>

					{isLoading && (
						<div className={styles.loading}>{t("history.loading")}</div>
					)}

					{error && <div className={styles.error}>{error}</div>}

					{!isLoading && !error && chats.length === 0 && (
						<div className={styles.empty}>
							<div className={styles.emptyIcon}>💬</div>
							<p className={styles.emptyText}>{t("history.emptyTitle")}</p>
							<p className={styles.emptySubtext}>
								{t("history.emptyDescription")}
							</p>
						</div>
					)}

					{!isLoading && !error && chats.length > 0 && (
						<div className={styles.chatList}>
							{sortedChats.map((chat) => (
								<button
									key={chat.id}
									type="button"
									className={styles.chatCard}
									onClick={() => handleChatClick(chat.id)}
								>
									<div className={styles.chatHeader}>
										<h2 className={styles.chatTitle}>
											{chat.title || t("history.untitledChat")}
										</h2>
										<span className={styles.chatDate}>
											{formatDate(chat.createdAt)}
										</span>
									</div>
									{chat.lastMessage &&
										(() => {
											const textContent = extractTextFromContent(
												chat.lastMessage.content,
											);
											return textContent ? (
												<p className={styles.chatMessage}>
													{truncateMessage(textContent)}
												</p>
											) : null;
										})()}
								</button>
							))}
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
